import { useEffect, useRef } from "react";

import LoadingPage from "@/components/ui/LoadingPage";
import useSsoLogin from "@/hooks/auth/useSsoLogin";
import { clearGlobalLogoutPending, getBasePath } from "@/utils/sso";

/**
 * Landing page after a completed global logout. The IdP redirects here via
 * `post_logout_redirect_uri` once its SSO session is gone, allowing a fresh
 * sign-in to prompt for credentials.
 *
 * Kept outside `ProtectedRoute` to prevent automatic SSO from sending the user
 * back into the app. Redirects to the auth app's login page instead of
 * `/authorize`, which may silently redirect if a session still exists.
 */
const LoggedOutPage = () => {
  const { redirectToAuthLogin } = useSsoLogin();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    // The hand-off to the IdP is over; auto-start may arm again from here on.
    clearGlobalLogoutPending();

    // Always the index route: signing back in after an explicit logout starts a
    // fresh session at the app root by design, so the page the user logged out
    // from is never carried across. Passing it explicitly also keeps the default
    // (`location.href`) from aiming the login back at this landing page.
    const redirectTo = `${globalThis.location.origin}${getBasePath()}/`;

    // If even the redirect can't be built (provider lookup down), fall back to
    // the password login instead of stranding the user on a loader.
    redirectToAuthLogin(redirectTo).catch(() => {
      globalThis.location.replace(
        `${globalThis.location.origin}${getBasePath()}/login`
      );
    });
    // Run once per landing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <LoadingPage />;
};

export default LoggedOutPage;
